<template>
    <Modal class="customer-marketing-preferences-modal"
           icon="fa-envelope"
           :ok="okClick"
           :cancel="cancelClick"
           title="MARKETING_PREFERENCES.MODAL_TITLE">
        <!-- Questions being answered on customer screen -->
        <div v-if="askingOnCustomerScreen"
             class="customer-screen-questions">
            <h4 class="mt-1 mb-2 text-center">
                {{ l('MARKETING_PREFERENCES.ASKING_CUSTOMER') }}
            </h4>

            <ul class="customer-marketing-questions">
                <li v-for="q in questions"
                    :key="q.preference.ID">
                    <span v-if="q.answer !== null">
                        {{ l('MARKETING_PREFERENCES.QUESTION_ANSWERED') }}
                        <span v-if="q.answer === true">
                            <i class="fa-solid fa-check"></i>
                        </span>
                        <span v-if="q.answer === false">
                            <i class="fa-solid fa-circle-x"></i>
                        </span>
                    </span>
                    <span v-else>
                        <span v-if="q.screenQuestion.wasDisplayed">
                            {{ l('MARKETING_PREFERENCES.QUESTION_DISPLAYED') }}
                        </span>
                        <span v-else-if="q.screenQuestion.wasReceived">
                            {{ l('MARKETING_PREFERENCES.QUESTION_SENT') }}
                        </span>
                        <span v-else>
                            {{ l('MARKETING_PREFERENCES.QUESTION_SENDING') }}
                        </span>
                        <i class="fa-solid fa-spinner fa-spin"></i>
                    </span>
                    {{ q.preference.question }}
                </li>
            </ul>
        </div>
        <!-- Questions being answered by the till operator here -->
        <div v-else
             class="operator-questions">
            <h4 class="mt-1 mb-2 text-center">
                {{ l('MARKETING_PREFERENCES.ASKING_USER') }}
            </h4>

            <ul class="customer-marketing-questions">
                <li v-for="q in questions"
                    :key="q.preference.ID">
                    <label>
                        <span>{{ l('MARKETING_PREFERENCES.CLICK_TO_SELECT') }}</span>
                        {{ q.preference.question }}
                        <input v-model="manualAnswers[q.preference.ID]"
                               type="checkbox" />
                    </label>
                </li>
            </ul>
        </div>
    </Modal>
</template>

<script>
import Modal from '@/vue/components/Modals/Modal';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: {
        Modal,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        questions: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            askingOnCustomerScreen: true,
            manualAnswers: {},
        };
    },

    created() {
        if (container.currentDevice.device.customerTouchScreenEnabled) {
            // Send questions to the customer screen.
            container.marketingPreferenceAsker.askOnCustomerScreen(this.questions)
                .then((selectedMarketingPreferenceIDs) => {
                    // Resolves when customer has answered.
                    this.resolve(selectedMarketingPreferenceIDs);
                })
                // Rejects if customer screen not available and user needs to manually answer.
                .catch(() => {
                    this.showManualQuestionScreen();
                });
        } else {
            this.showManualQuestionScreen();
        }
    },

    methods: {
        /**
         * Setup the manual question screen where the operator can complete marketing questions.
         * This is triggered when either the customer screen is unresponsive or is not touch enabled.
         */
        showManualQuestionScreen() {
            this.askingOnCustomerScreen = false;
            // Build the answer object.
            this.questions.forEach((q) => {
                this.manualAnswers[q.preference.ID] = false;
            });
        },

        okClick() {
            // Convert the {marketingPreferenceID:boolean} object to a simple array containing
            // the IDs that were selected.
            const selectedMarketingPreferenceIDs = [];
            for (const marketingPreferenceID in this.manualAnswers) {
                if (
                    Object.prototype.hasOwnProperty.call(this.manualAnswers, marketingPreferenceID)
                    && this.manualAnswers[marketingPreferenceID] === true
                ) {
                    selectedMarketingPreferenceIDs.push(marketingPreferenceID);
                }
            }
            this.resolve(selectedMarketingPreferenceIDs);
        },

        async cancelClick() {
            await container.marketingPreferenceAsker.customerScreenDataService.cancelQuestions();

            this.cancel();
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

.customer-marketing-preferences-modal {
    .customer-marketing-questions {
        list-style: none;
        font-size: 18px;
        margin: 0;
        padding: 0;
        text-align: left;

        li {
            margin-bottom: @gutter-width;
            background: @form-group-container-bg;
            padding: 10px 50px 10px 10px;
            position: relative;
            border-radius: @border-radius-base;

            &:last-child {
                margin-bottom: 0;
            }
        }

        span {
            font-size: @font-size-medium;
            color: @text-muted;
            display: block;
        }

        label {
            margin: 0;
        }

        i {
            font-size: 23px;
            margin-right: 5px;
            position: absolute;
            right: 10px;
            top: 20px;
            color: @text-color;
            z-index: 1;

            &.fa-check {
                color: @brand-success;
            }

            &.fa-circle-x {
                color: @brand-danger;
            }
        }

        input[type=checkbox] {
            position: absolute;
            top: 50%;
            right: 10px;
            width: 32px;
            height: 32px;
            z-index: 2;
            cursor: pointer;
            margin: -15px 0 0 0; // 15px looks more centered than 16px
        }
    }

    .operator-questions {
        li {
            padding: 0;
        }

        label {
            // Move the padding from the <li> to the <label> so the whole area is clickable.
            padding: 10px 50px 10px 10px;

            display: block;
            cursor: pointer;
        }
    }
}
</style>
